/**
 * Editor de template (Brief 4 · Tela 3 · PR1 leitura → PR2 mutação).
 *
 * Leitura (sem `message-template-manage`) vs edição (com a cap): defense-in-depth
 * — o gate está na AÇÃO (mutation + render), não só no botão. COM a cap:
 *   - Conteúdo (nome/categoria/header/body/footer/botões) é editável só em
 *     `draft`/`rejected` (backend recusa o resto com 422 `content-not-editable`);
 *     nos demais status os campos ficam desabilitados.
 *   - O toggle `enabled` (Habilitar/Desabilitar) funciona em QUALQUER status —
 *     é o "Desabilitar" do D-6 aprovado (ortogonal à aprovação Meta).
 *   - "Salvar rascunho" (PATCH) e "Submeter à Meta" (POST submit-meta) com
 *     If-Match (ADR-071); 409 vira banner de conflito NÃO-destrutivo (recarregar,
 *     nunca sobrescrever cego); 422 reflete o erro REAL da Meta (sem ✓ fabricado);
 *     502 (dev sem credencial Meta) é estado HONESTO via toast, não bug.
 *
 * Honest-defer: "Histórico de envios" (`usageStats` null) e a config do cron D-6
 * (envio automático/modo de condução) vivem no lifecycle da Conversation, fora do
 * model do template — nota distinta, não dado fabricado.
 *
 * O preview WhatsApp ao vivo reflete o estado atual do form enquanto se edita.
 */

import { useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import { CATEGORY_DESCRIPTIONS, categoryLabel, statusMeta } from './labels';
import {
  buildComponents,
  countVariables,
  flattenComponents,
  isContentEditable,
} from './template-helpers';
import { useSubmitTemplateToMeta, useUpdateTemplate } from './use-templates';
import { WhatsAppPreview } from './WhatsAppPreview';

import type { FlatTemplate } from './template-helpers';
import type { TemplateButton, TemplateCategory, TemplateDto, UpdateTemplateBody } from './types';
import type { ReactNode } from 'react';

const CATEGORIES: TemplateCategory[] = ['authentication', 'utility', 'marketing'];

interface FormState extends FlatTemplate {
  name: string;
  category: TemplateCategory;
}

function initForm(template: TemplateDto): FormState {
  const flat = flattenComponents(template.components);
  return { ...flat, name: template.name, category: template.category };
}

function buildPatch(template: TemplateDto, form: FormState): UpdateTemplateBody | null {
  const original = initForm(template);
  const patch: UpdateTemplateBody = {};
  if (form.name !== original.name) patch.name = form.name;
  if (form.category !== original.category) patch.category = form.category;
  const contentChanged =
    form.header !== original.header ||
    form.body !== original.body ||
    form.footer !== original.footer ||
    JSON.stringify(form.buttons) !== JSON.stringify(original.buttons);
  if (contentChanged) {
    patch.components = buildComponents({
      header: form.header,
      body: form.body,
      footer: form.footer,
      buttons: form.buttons,
    });
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

interface TemplateEditorViewProps {
  template: TemplateDto;
  brand: string;
  canManage: boolean;
  onBack: () => void;
}

export function TemplateEditorView({
  template,
  brand,
  canManage,
  onBack,
}: TemplateEditorViewProps): JSX.Element {
  const [form, setForm] = useState<FormState>(() => initForm(template));
  const [conflict, setConflict] = useState<{ currentVersion: number | null } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const updateMutation = useUpdateTemplate(template.id);
  const submitMutation = useSubmitTemplateToMeta(template.id);
  const toast = useToastStore((s) => s.push);

  const meta = statusMeta(template.status);
  const contentEditable = canManage && isContentEditable(template.status);
  const flatForPreview: FlatTemplate = {
    header: form.header,
    body: form.body,
    footer: form.footer,
    buttons: form.buttons,
  };
  const vars = countVariables(form.body);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  const handleApiError = (err: unknown, fallback: string): void => {
    if (err instanceof ApiError && err.status === 409) {
      const body = err.body as { currentVersion?: number };
      setConflict({ currentVersion: body.currentVersion ?? null });
      return;
    }
    if (err instanceof ApiError && err.status === 422) {
      // 422 real da Meta/backend — refletir a mensagem, não fabricar ✓.
      setValidationError(err.message.length > 0 ? err.message : 'Validação recusada pelo backend.');
      return;
    }
    if (err instanceof ApiError && err.status === 502) {
      // Submissão indisponível em dev (sem credencial Meta) — estado honesto.
      toast({
        variant: 'error',
        title: 'Submissão indisponível',
        description:
          'Cadastro Tech Provider Meta pendente — o template segue como rascunho. Tente após o cadastro ativo.',
        durationMs: 6000,
      });
      return;
    }
    toast({ variant: 'error', title: fallback, durationMs: 6000 });
  };

  const handleSaveDraft = (): void => {
    const patch = buildPatch(template, form);
    if (patch === null) return;
    setValidationError(null);
    updateMutation.mutate(
      { expectedVersion: template.version, body: patch },
      {
        onError: (err) => handleApiError(err, 'Não foi possível salvar o rascunho.'),
        onSuccess: () => {
          setConflict(null);
          toast({ variant: 'success', title: 'Rascunho salvo', durationMs: 3000 });
        },
      },
    );
  };

  const handleSubmitMeta = (): void => {
    setValidationError(null);
    // Salva o conteúdo pendente antes de submeter (se houver), depois submete.
    const patch = buildPatch(template, form);
    const doSubmit = (version: number): void => {
      submitMutation.mutate(
        { expectedVersion: version },
        {
          onError: (err) => handleApiError(err, 'Não foi possível submeter à Meta.'),
          onSuccess: () => {
            setConflict(null);
            toast({
              variant: 'success',
              title: 'Submetido à Meta',
              description: 'Aguardando aprovação (assíncrona).',
              durationMs: 4000,
            });
          },
        },
      );
    };
    if (patch !== null) {
      updateMutation.mutate(
        { expectedVersion: template.version, body: patch },
        {
          onError: (err) => handleApiError(err, 'Não foi possível salvar antes de submeter.'),
          onSuccess: (updated) => doSubmit(updated.version),
        },
      );
    } else {
      doSubmit(template.version);
    }
  };

  const handleToggleEnabled = (): void => {
    updateMutation.mutate(
      { expectedVersion: template.version, body: { enabled: !template.enabled } },
      {
        onError: (err) => handleApiError(err, 'Não foi possível alterar o status de uso.'),
        onSuccess: () => {
          setConflict(null);
          toast({
            variant: 'success',
            title: template.enabled ? 'Template desabilitado' : 'Template habilitado',
            durationMs: 3000,
          });
        },
      },
    );
  };

  const busy = updateMutation.isPending || submitMutation.isPending;
  const dirty = buildPatch(template, form) !== null;
  const canSubmit = template.status === 'draft' || template.status === 'rejected';

  return (
    <div className="message-templates-screen">
      <div className="mt-pagehead">
        <div className="mt-pagehead__heading">
          <button type="button" className="mt-btn mt-btn--ghost mt-btn--sm" onClick={onBack}>
            ← Voltar
          </button>
          <h1 className="mt-pagehead__title">{template.name}</h1>
          <p className="mt-pagehead__sub">
            <span className={`mt-pill mt-pill--${meta.semaforo}`}>
              {meta.glyph} {meta.label}
            </span>
            {template.isD6 && <span className="mt-d6-badge">D-6</span>}
          </p>
        </div>
      </div>

      <div className="mt-page">
        {conflict !== null && (
          <div className="mt-banner mt-banner--danger" role="alert">
            <span>
              ⚠ Este template foi alterado por outra pessoa
              {conflict.currentVersion !== null
                ? ` (versão atual: v${conflict.currentVersion.toString()})`
                : ''}
              . Recarregue para ver a versão mais recente — suas alterações não foram sobrescritas.{' '}
              <button type="button" className="mt-btn mt-btn--sm" onClick={onBack}>
                Voltar à lista
              </button>
            </span>
          </div>
        )}

        {template.status === 'rejected' && template.rejectionReason !== null && (
          <div className="mt-banner mt-banner--danger" role="alert">
            <span>
              <strong>Rejeitado pela Meta:</strong> {template.rejectionReason} Ajuste e resubmeta.
            </span>
          </div>
        )}

        {validationError !== null && (
          <div className="mt-banner mt-banner--warning" role="alert">
            <span>
              <strong>Validação recusada:</strong> {validationError}
            </span>
          </div>
        )}

        <div className="mt-editor-grid">
          <div className="mt-panel">
            <Field label="Nome (regras Meta — lowercase + underscores)">
              <input
                className="mt-input"
                value={form.name}
                disabled={!contentEditable}
                onChange={(e) => set('name', e.target.value)}
              />
            </Field>

            <div className="mt-field">
              <span className="mt-field__label">Categoria</span>
              <div className="mt-radio-row">
                {CATEGORIES.map((c) => (
                  <label
                    key={c}
                    className={`mt-radio${form.category === c ? ' mt-radio--selected' : ''}${
                      contentEditable ? '' : ' mt-radio--disabled'
                    }`}
                  >
                    <input
                      type="radio"
                      name="mt-category"
                      checked={form.category === c}
                      disabled={!contentEditable}
                      onChange={() => set('category', c)}
                    />
                    <span className="mt-radio__main">
                      <span className="mt-radio__label">{categoryLabel(c)}</span>
                      <span className="mt-radio__desc">{CATEGORY_DESCRIPTIONS[c]}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <Field label="Header (opcional)">
              <input
                className="mt-input"
                value={form.header}
                disabled={!contentEditable}
                placeholder="ex: Sua instalação está agendada"
                onChange={(e) => set('header', e.target.value)}
              />
            </Field>

            <Field
              label={`Body (obrigatório · variáveis em sequência {{1}}, {{2}}… · ${vars.toString()} detectada(s))`}
            >
              <textarea
                className="mt-textarea"
                rows={4}
                value={form.body}
                disabled={!contentEditable}
                onChange={(e) => set('body', e.target.value)}
              />
            </Field>

            <Field label="Footer (opcional)">
              <input
                className="mt-input"
                value={form.footer}
                disabled={!contentEditable}
                onChange={(e) => set('footer', e.target.value)}
              />
            </Field>

            <ButtonsEditor
              buttons={form.buttons}
              editable={contentEditable}
              onChange={(buttons) => set('buttons', buttons)}
            />

            {canManage && (
              <div className="mt-actions">
                <button
                  type="button"
                  className="mt-btn"
                  disabled={busy || !dirty || !contentEditable}
                  onClick={handleSaveDraft}
                >
                  Salvar rascunho
                </button>
                {canSubmit && (
                  <button
                    type="button"
                    className="mt-btn mt-btn--primary"
                    disabled={busy}
                    onClick={handleSubmitMeta}
                  >
                    Submeter à Meta
                  </button>
                )}
                <button
                  type="button"
                  className="mt-btn mt-btn--ghost"
                  disabled={busy}
                  onClick={handleToggleEnabled}
                >
                  {template.enabled ? 'Desabilitar' : 'Habilitar'}
                </button>
              </div>
            )}

            {!contentEditable && (
              <p className="mt-defer-note">
                Conteúdo travado: só templates em rascunho ou rejeitados podem ser editados (regra
                da Meta). O toggle de uso (Habilitar/Desabilitar) continua disponível.
              </p>
            )}
          </div>

          <aside className="mt-preview-aside">
            <div className="mt-preview-aside__label">Preview WhatsApp ao vivo</div>
            <WhatsAppPreview
              brand={brand}
              flat={flatForPreview}
              approved={template.status === 'approved'}
            />
            {template.isD6 && (
              <div className="mt-d6-defer">
                {/* honest-defer: cron + histórico fora do model do template. */}
                <p className="mt-defer-note">
                  Envio automático (6 dias antes do timeout) e modo de condução são configurados no
                  ciclo de vida da conversa, não aqui.
                </p>
                <p className="mt-defer-note">Histórico de envios indisponível (sem fonte ainda).</p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="mt-field">
      <span className="mt-field__label">{label}</span>
      {children}
    </div>
  );
}

function ButtonsEditor({
  buttons,
  editable,
  onChange,
}: {
  buttons: TemplateButton[];
  editable: boolean;
  onChange: (buttons: TemplateButton[]) => void;
}): JSX.Element | null {
  if (!editable && buttons.length === 0) return null;
  return (
    <div className="mt-field">
      <span className="mt-field__label">Botões (resposta rápida)</span>
      <div className="mt-chip-row">
        {buttons.map((b, i) => (
          <span key={`${b.text}-${i.toString()}`} className="mt-chip">
            {editable ? (
              <input
                className="mt-chip__input"
                value={b.text}
                aria-label={`Texto do botão ${(i + 1).toString()}`}
                onChange={(e) => {
                  const next = buttons.slice();
                  next[i] = { ...b, text: e.target.value };
                  onChange(next);
                }}
              />
            ) : (
              b.text
            )}
            <span className="mt-chip__type">{b.type}</span>
            {editable && (
              <button
                type="button"
                className="mt-chip__x"
                aria-label={`Remover botão ${b.text}`}
                onClick={() => onChange(buttons.filter((_, j) => j !== i))}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {editable && buttons.length < 3 && (
          <button
            type="button"
            className="mt-btn mt-btn--ghost mt-btn--sm"
            onClick={() => onChange([...buttons, { type: 'QUICK_REPLY', text: 'Novo botão' }])}
          >
            + Botão
          </button>
        )}
      </div>
    </div>
  );
}
